import { create } from 'zustand';
import type { Session } from '../services/auth';
import { preferences } from '../services/storage';

/**
 * Global state.
 *
 * Four narrow stores, not one. Records live in TanStack Query — putting an
 * employee list in Zustand means owning cache invalidation by hand, which is
 * how prototypes end up showing stale data after a mutation.
 *
 * What belongs here is state that is genuinely global and not server-owned:
 * who is signed in, whether the sidebar is collapsed, what the Simulation
 * Center has switched on.
 */

/* -------------------------------------------------------------------------- */
/* Session                                                                     */
/* -------------------------------------------------------------------------- */

interface AuthState {
  session: Session | null;
  ready: boolean;
  setSession: (session: Session | null) => void;
  setReady: (ready: boolean) => void;
}

/*
 * Who last held a session in this tab, kept past sign-out.
 *
 * Signing in lands where the tab already was only for the same person coming
 * back. Somebody else signing in on that address was shown the previous
 * user's page — and, as often as not, a refusal to open it.
 */
const LAST_USER_KEY = 'micarahr.lastUserId';

function rememberUser(userId: string): void {
  try {
    sessionStorage.setItem(LAST_USER_KEY, userId);
  } catch {
    // Storage refused (a private window); the next sign-in simply goes home.
  }
}

/** The account that last held a session in this tab, if it is known. */
export function lastSignedInUserId(): string | null {
  try {
    return sessionStorage.getItem(LAST_USER_KEY);
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  ready: false,
  setSession: (session) => {
    if (session) rememberUser(session.userId);
    set({ session });
  },
  setReady: (ready) => set({ ready }),
}));

/** The signed-in session, or null. */
export function useSession(): Session | null {
  return useAuthStore((state) => state.session);
}

/**
 * Permission check bound to the current session.
 *
 * Navigation uses this to decide what to show. It is *not* the enforcement
 * point — services check permission again, because hiding a link is
 * presentation and a guard is a guard (§6).
 */
export function usePermission(): {
  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
  session: Session | null;
} {
  const session = useAuthStore((state) => state.session);
  const granted = new Set(session?.permissions ?? []);
  return {
    session,
    can: (permission: string) => granted.has(permission),
    canAny: (permissions: string[]) =>
      permissions.length === 0 || permissions.some((permission) => granted.has(permission)),
  };
}

/* -------------------------------------------------------------------------- */
/* Interface state                                                             */
/* -------------------------------------------------------------------------- */

export interface Toast {
  id: string;
  title: string;
  message?: string;
  tone: 'success' | 'danger' | 'info' | 'warning';
}

interface UiState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;

  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  dismissToast: (id: string) => void;
}

let toastSequence = 0;

export const useUiStore = create<UiState>((set) => ({
  // Read synchronously so the shell renders at the right width immediately.
  sidebarCollapsed: preferences.read('sidebarCollapsed', false),
  toggleSidebar: () =>
    set((state) => {
      const next = !state.sidebarCollapsed;
      preferences.write('sidebarCollapsed', next);
      return { sidebarCollapsed: next };
    }),
  setSidebarCollapsed: (collapsed) => {
    preferences.write('sidebarCollapsed', collapsed);
    set({ sidebarCollapsed: collapsed });
  },

  commandPaletteOpen: false,
  setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),

  toasts: [],
  showToast: (toast) => {
    toastSequence += 1;
    const id = `toast_${toastSequence}`;
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));
    setTimeout(() => {
      set((state) => ({ toasts: state.toasts.filter((entry) => entry.id !== id) }));
    }, 5000);
  },
  dismissToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),
}));

export function useToast() {
  return useUiStore((state) => state.showToast);
}

/* -------------------------------------------------------------------------- */
/* Workspace                                                                   */
/* -------------------------------------------------------------------------- */

interface WorkspaceState {
  /** Set once the demonstration data is loaded and services are usable. */
  dataReady: boolean;
  seeded: boolean;
  /** True when persistence failed and this session is in memory only. */
  degraded: boolean;
  setDataReady: (ready: boolean, seeded: boolean, degraded?: boolean) => void;
  lastRefreshedAt: string | null;
  setLastRefreshedAt: (at: string) => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set) => ({
  dataReady: false,
  seeded: false,
  degraded: false,
  setDataReady: (dataReady, seeded, degraded = false) => set({ dataReady, seeded, degraded }),
  lastRefreshedAt: null,
  setLastRefreshedAt: (lastRefreshedAt) => set({ lastRefreshedAt }),
}));

/* -------------------------------------------------------------------------- */
/* Simulation                                                                  */
/* -------------------------------------------------------------------------- */

export interface SimulationEvent {
  id: string;
  label: string;
  at: string;
  detail: string;
}

interface SimulationState {
  latencyMs: number;
  failAll: boolean;
  failingServices: string[];
  events: SimulationEvent[];
  setLatency: (ms: number) => void;
  setFailAll: (failing: boolean) => void;
  toggleService: (service: string) => void;
  pushEvent: (event: Omit<SimulationEvent, 'id'>) => void;
  clearEvents: () => void;
}

let simulationSequence = 0;

export const useSimulationStore = create<SimulationState>((set) => ({
  latencyMs: 120,
  failAll: false,
  failingServices: [],
  events: [],
  setLatency: (latencyMs) => set({ latencyMs }),
  setFailAll: (failAll) => set({ failAll }),
  toggleService: (service) =>
    set((state) => ({
      failingServices: state.failingServices.includes(service)
        ? state.failingServices.filter((entry) => entry !== service)
        : [...state.failingServices, service],
    })),
  pushEvent: (event) => {
    simulationSequence += 1;
    set((state) => ({
      events: [{ ...event, id: `sim_${simulationSequence}` }, ...state.events].slice(0, 25),
    }));
  },
  clearEvents: () => set({ events: [] }),
}));
